import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useChessGame } from './hooks/useChessGame';
import { useScreenSize } from './hooks/useScreenSize';
import { playLobbyInviteSound, playPieceMoveSound } from './hooks/usePieceMoveSound';
import { GameSetupWizard } from './components/GameSetupWizard';
import { BlindSetupScreen } from './components/BlindSetupScreen';
import { BackgroundMusicPlayer } from './components/BackgroundMusicPlayer';

const GameControls = lazy(() => import('./components/GameControls').then((m) => ({ default: m.GameControls })));
const GameVariationHeader = lazy(() => import('./components/GameVariationHeader').then((m) => ({ default: m.GameVariationHeader })));
const ChessBoard = lazy(() => import('./components/ChessBoard').then((m) => ({ default: m.ChessBoard })));
const MoveHistory = lazy(() => import('./components/MoveHistory').then((m) => ({ default: m.MoveHistory })));
const PromotionDialog = lazy(() => import('./components/PromotionDialog').then((m) => ({ default: m.PromotionDialog })));

const ERROR_MESSAGES = {
  LOAD_LOBBY: 'Failed to load the online lobby.',
  JOIN_ROOM: 'Failed to join room.',
  CONNECT_ACCEPTED_GAME: 'Failed to connect to the accepted game.',
  CREATE_ROOM: 'Failed to create room.',
  LEAVE_ROOM: 'Failed to leave room.',
  SEND_INVITE: 'Failed to send invite.',
  ACCEPT_INVITE: 'Failed to accept invite.',
  CANCEL_INVITE: 'Failed to cancel invite.',
  REFUSE_INVITE: 'Failed to refuse invite.',
  MALFORMED_MESSAGE: 'Received malformed realtime message.',
} as const;
import {
  Color,
  GameMode,
  GameMove,
  OnlineAcceptInviteResponse,
  OnlineColorPreference,
  OnlineCreateInviteRequest,
  OnlineGameSnapshot,
  OnlineInviteResponse,
  OnlineLobbyPresenceResponse,
  OnlineLobbyServerMessage,
  OnlineLobbyState,
  OnlineRoomClientMessage,
  OnlineRoomConfig,
  OnlineRoomParticipantSession,
  OnlineRoomPresence,
  OnlineRoomServerMessage,
  OnlineRoomSession,
  OnlineUser,
  OnlineRoomMetadata,
  PieceType,
  PlayerPieces,
} from './types';
import { chess960PositionFromId, randomChess960BackRank } from './utils/chess960Utils';
import { positionToAlgebraic } from './utils/boardUtils';
import { PIECE_NAMES, validateBlindBackRank } from './engine/chessEngine';

interface RoomCreateResponse {
  roomId: string;
  role: Color;
  whiteName: string;
  blackName: string | null;
  metadata?: OnlineRoomMetadata;
}

interface RoomJoinResponse {
  roomId: string;
  role: Color;
  config: OnlineRoomConfig;
  snapshot: OnlineGameSnapshot | null;
  whiteName: string;
  blackName: string | null;
  metadata?: OnlineRoomMetadata;
}

interface SessionResponse {
  user: OnlineUser | null;
}

interface BrowserSessionGameRecord {
  gameType: 'online' | 'over-the-board';
  players: {
    white: string;
    black: string;
  };
  variation: string;
  promotionRule: string;
  startPosition: {
    whiteBackRank: string;
    blackBackRank: string;
    chess960PositionId: number | null;
    isNonStandard: boolean;
  };
  moves: string[];
  text: string;
  updatedAt: number;
}

const SESSION_TOKEN_KEY = 'chess960x32_token';
const ROOM_PERSIST_KEY = 'chess960x32_room';
const GAME_RECORD_SESSION_KEY = 'chess960x32_game_record';
const SOCKET_HEARTBEAT_TIMEOUT_MS = 90_000;
const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '::1']);

interface PersistedRoom {
  roomCode: string;
  role: Color;
}

const EMPTY_LOBBY_STATE: OnlineLobbyState = {
  self: null,
  users: [],
  incomingInvites: [],
  outgoingInvites: [],
};

const isLocalHostname = (hostname: string): boolean => LOCAL_HOSTNAMES.has(hostname);

const isLocalUrl = (value: string): boolean => {
  try {
    return isLocalHostname(new URL(value).hostname);
  } catch {
    return false;
  }
};

const getStoredToken = (): string | null => {
  try {
    return localStorage.getItem(SESSION_TOKEN_KEY);
  } catch {
    return null;
  }
};

const storeToken = (token: string): void => {
  try {
    localStorage.setItem(SESSION_TOKEN_KEY, token);
  } catch { /* ignore */ }
};

const clearToken = (): void => {
  try {
    localStorage.removeItem(SESSION_TOKEN_KEY);
  } catch { /* ignore */ }
};

const getStoredRoom = (): PersistedRoom | null => {
  try {
    const raw = localStorage.getItem(ROOM_PERSIST_KEY);
    return raw ? JSON.parse(raw) as PersistedRoom : null;
  } catch {
    return null;
  }
};

const storeRoom = (room: PersistedRoom): void => {
  try {
    localStorage.setItem(ROOM_PERSIST_KEY, JSON.stringify(room));
  } catch { /* ignore */ }
};

const clearStoredRoom = (): void => {
  try {
    localStorage.removeItem(ROOM_PERSIST_KEY);
  } catch { /* ignore */ }
};

const getStoredGameRecord = (): BrowserSessionGameRecord | null => {
  try {
    const raw = sessionStorage.getItem(GAME_RECORD_SESSION_KEY);
    return raw ? JSON.parse(raw) as BrowserSessionGameRecord : null;
  } catch {
    return null;
  }
};

const storeGameRecord = (record: BrowserSessionGameRecord): void => {
  try {
    sessionStorage.setItem(GAME_RECORD_SESSION_KEY, JSON.stringify(record));
  } catch { /* ignore */ }
};

const clearStoredGameRecord = (): void => {
  try {
    sessionStorage.removeItem(GAME_RECORD_SESSION_KEY);
  } catch { /* ignore */ }
};

const isConfigReady = (config: OnlineRoomConfig): boolean => {
  const standardOr960 = ['chess', 'chessx2', 'chess960', 'chess960x2'].includes(config.gameVariation);
  return standardOr960 || Boolean(config.playerPieces.white && config.playerPieces.black);
};

const STANDARD_BACK_RANK: PieceType[] = ['R', 'N', 'B', 'Q', 'K', 'B', 'N', 'R'];

/** The single shared major piece for Blind Chess960 configs. */
const sharedPieceForBlind = (pieces: PlayerPieces): PieceType => (pieces.white ?? pieces.black ?? 'Q');

const usesCustomQueenReplacement = (variation: string): boolean => (
  variation !== 'chess' && variation !== 'chessx2' && variation !== 'chess960'
);

const buildStartingBackRanks = (
  variation: string,
  playerPieces: { white: PieceType | null; black: PieceType | null },
  chess960PositionId: number | null,
): { white: string; black: string } => {
  const baseRank = variation.includes('960')
    ? chess960PositionFromId(chess960PositionId ?? 518)
    : STANDARD_BACK_RANK;

  const white = baseRank.map((piece) => {
    if (piece === 'Q' && usesCustomQueenReplacement(variation) && playerPieces.white) {
      return playerPieces.white;
    }
    return piece;
  }).join('');

  const black = baseRank.map((piece) => {
    if (piece === 'Q' && usesCustomQueenReplacement(variation) && playerPieces.black) {
      return playerPieces.black;
    }
    return piece;
  }).join('');

  return { white, black };
};

const formatVariationLabel = (variation: string): string => variation.toUpperCase();

const formatMovesForExport = (moves: GameMove[]): string => {
  if (moves.length === 0) {
    return 'No moves recorded.';
  }

  return Array.from({ length: Math.ceil(moves.length / 2) }, (_, moveNumber) => {
    const whiteMove = moves[moveNumber * 2]?.notation ?? '';
    const blackMove = moves[moveNumber * 2 + 1]?.notation;

    return blackMove
      ? `${moveNumber + 1}. ${whiteMove} ${blackMove}`
      : `${moveNumber + 1}. ${whiteMove}`;
  }).join('\n');
};

const Chess = () => {
  const { isCompactMode } = useScreenSize();
  
  const {
    gameVariation,
    setGameVariation,
    gamePhase,
    playerPieces,
    setPlayerPieces,
    promotionRule,
    setPromotionRule,
    board,
    currentPlayer,
    selectedSquare,
    validMoves,
    gameHistory,
    draggedPiece,
    draggedFrom,
    isTouchDevice,
    isDragging,
    getGameStatus,
    startGame,
    startOnlineGame,
    prepareBlindSetup,
    resetGame,
    resignGame,
    queueDrawOffer,
    cancelQueuedDrawOffer,
    acceptDrawOffer,
    handleSquareClick,
    handleDragStart,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDragEnd,
    handleDrop,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handlePromotion,
    getPromotionPieces,
    setPlayerControlColor,
    getOnlineSnapshot,
    applyOnlineSnapshot,
    lastSentSnapshotRef,
    snapshotVersionRef,
    pendingResyncRef,
    drawOfferedBy,
    drawAgreed,
    offerDrawOnNextMove,
    // Timer props
    whiteTimeMs,
    blackTimeMs,
    timeControl,
    setTimeControl,
    turnStartMs,
    timeoutWinner,
    formatTime,
    forceTimerStart,
  } = useChessGame();

  const [gameMode, setGameMode] = useState<GameMode>('online');
  const [onlineEntryMode, setOnlineEntryMode] = useState<'lobby' | 'private'>('lobby');
  const [onlineUser, setOnlineUser] = useState<OnlineUser | null>(null);
  const [onlineRoom, setOnlineRoom] = useState<OnlineRoomParticipantSession | null>(null);
  const [onlineRoomCode, setOnlineRoomCode] = useState<string>('');
  const [onlineStatus, setOnlineStatus] = useState<string>('');
  const [onlineError, setOnlineError] = useState<string | null>(null);
  const [onlineBusy, setOnlineBusy] = useState<boolean>(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [roomPresence, setRoomPresence] = useState<OnlineRoomPresence | null>(null);
  const [lobbyState, setLobbyState] = useState<OnlineLobbyState>(EMPTY_LOBBY_STATE);
  const [selectedLobbyUserId, setSelectedLobbyUserId] = useState<string | null>(null);
  const [inviteColorPreference, setInviteColorPreference] = useState<OnlineColorPreference>('white');
  const [chess960PositionId, setChess960PositionId] = useState<number | null>(null);
  const [gameRecordFeedback, setGameRecordFeedback] = useState<string | null>(null);
  // Blind Chess960 setup state
  const [blindRank, setBlindRank] = useState<string>('RNBQKBNR');
  const [blindSetupSubmitted, setBlindSetupSubmitted] = useState<boolean>(false);
  const [blindOpponentSubmitted, setBlindOpponentSubmitted] = useState<boolean>(false);
  // Incremented when server sends game_started; useEffect reacts with fresh state
  const [gameStartedSignal, setGameStartedSignal] = useState(0);

  const wsRef = useRef<WebSocket | null>(null);
  const lobbyWsRef = useRef<WebSocket | null>(null);
  const tokenRef = useRef<string | null>(getStoredToken());
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef<number>(0);
  const heartbeatTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const connectSocketRef = useRef<(roomId: string) => void>(() => {});
  const lobbyReconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lobbyReconnectAttemptsRef = useRef<number>(0);
  const lobbyHeartbeatTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const connectLobbySocketRef = useRef<() => void>(() => {});
  const pendingUrlJoinRef = useRef<string | null>(null);
  const autoJoinRoomRef = useRef<string | null>(null);
  const activeRoomIdRef = useRef<string | null>(null);
  const gameModeRef = useRef<GameMode>('local');
  const onlineUserRef = useRef<OnlineUser | null>(null);
  const lastNotifiedInviteIdRef = useRef<string | null>(null);
  // Live view of the phase for the long-lived room-socket message handler.
  const gamePhaseRef = useRef(gamePhase);
  useEffect(() => {
    gamePhaseRef.current = gamePhase;
  }, [gamePhase]);
  // Live view of the participant role for the long-lived room-socket message
  // handler. The socket is created before the room state flushes, so the
  // closure's onlineRoom?.role is stale/null — and it flips on rematch.
  const onlineRoleRef = useRef<Color | null>(null);
  useEffect(() => {
    onlineRoleRef.current = onlineRoom?.role ?? null;
  }, [onlineRoom?.role]);

  // Play wooden piece-drop sound on every new move
  useEffect(() => {
    if (gameHistory.length > 0) {
      playPieceMoveSound();
    }
  }, [gameHistory.length]);

  useEffect(() => {
    if (!gameRecordFeedback) return;

    const timeoutId = window.setTimeout(() => {
      setGameRecordFeedback(null);
    }, 2200);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [gameRecordFeedback]);

  useEffect(() => {
    const url = new URL(window.location.href);
    const token = url.searchParams.get('token');
    const roomParam = url.searchParams.get('room');
    if (token) {
      storeToken(token);
      tokenRef.current = token;
      url.searchParams.delete('token');
    }
    if (roomParam) {
      const normalizedRoom = roomParam.trim().toUpperCase();
      setOnlineRoomCode(normalizedRoom);
      setGameMode('online');
      setOnlineEntryMode('private');
      pendingUrlJoinRef.current = normalizedRoom;
      url.searchParams.delete('room');
    }
    if (token || roomParam) {
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  useEffect(() => {
    activeRoomIdRef.current = onlineRoom?.roomId ?? null;
  }, [onlineRoom?.roomId]);

  useEffect(() => {
    gameModeRef.current = gameMode;
  }, [gameMode]);

  useEffect(() => {
    onlineUserRef.current = onlineUser;
  }, [onlineUser]);

  const authHeaders = useCallback((): Record<string, string> => {
    const token = tokenRef.current;
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const backendBaseUrl = useMemo(() => {
    const isLocalFrontend = isLocalHostname(window.location.hostname);
    const fallbackBaseUrl = isLocalFrontend ? 'http://localhost:8000' : 'https://chess960x32.topce.deno.net';
    const configuredBaseUrl = import.meta.env.VITE_ONLINE_BACKEND_URL?.trim();
    const rawBaseUrl = configuredBaseUrl && !(isLocalUrl(configuredBaseUrl) && !isLocalFrontend)
      ? configuredBaseUrl
      : fallbackBaseUrl;
    return rawBaseUrl.replace(/\/$/, '');
  }, []);

  const wsBaseUrl = useMemo(() => (
    backendBaseUrl.startsWith('https://')
      ? backendBaseUrl.replace(/^https:\/\//, 'wss://')
      : backendBaseUrl.replace(/^http:\/\//, 'ws://')
  ), [backendBaseUrl]);

  const returnTo = useMemo(() => {
    const url = new URL(window.location.href);
    url.searchParams.delete('token');
    if (onlineRoomCode) {
      url.searchParams.set('room', onlineRoomCode);
    }
    return encodeURIComponent(url.toString());
  }, [onlineRoomCode]);
  const githubLoginUrl = `${backendBaseUrl}/auth/github/start?returnTo=${returnTo}`;
  const googleLoginUrl = `${backendBaseUrl}/auth/google/start?returnTo=${returnTo}`;
  const facebookLoginUrl = `${backendBaseUrl}/auth/facebook/start?returnTo=${returnTo}`;
  const lichessLoginUrl = `${backendBaseUrl}/auth/lichess/start?returnTo=${returnTo}`;
  const logoutUrl = `${backendBaseUrl}/auth/logout?returnTo=${returnTo}`;

  const cancelReconnect = useCallback((): void => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    reconnectAttemptsRef.current = 0;
  }, []);

  const cancelLobbyReconnect = useCallback((): void => {
    if (lobbyReconnectTimeoutRef.current) {
      clearTimeout(lobbyReconnectTimeoutRef.current);
      lobbyReconnectTimeoutRef.current = null;
    }
    lobbyReconnectAttemptsRef.current = 0;
  }, []);

  const closeRoomSocket = useCallback((): void => {
    cancelReconnect();
    if (heartbeatTimeoutRef.current) {
      clearTimeout(heartbeatTimeoutRef.current);
      heartbeatTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }
    setWsConnected(false);
  }, [cancelReconnect]);

  const handleRoomClosedLocally = useCallback((message: string, nextEntryMode: 'lobby' | 'private'): void => {
    closeRoomSocket();
    clearStoredRoom();
    setOnlineRoom(null);
    setRoomPresence(null);
    setOnlineRoomCode('');
    setOnlineError(null);
    setOnlineStatus(message);
    autoJoinRoomRef.current = null;
    activeRoomIdRef.current = null;
    resetGame();
    setGameMode('online');
    setOnlineEntryMode(nextEntryMode);
  }, [closeRoomSocket, resetGame]);

  const closeLobbySocket = useCallback((): void => {
    cancelLobbyReconnect();
    if (lobbyHeartbeatTimeoutRef.current) {
      clearTimeout(lobbyHeartbeatTimeoutRef.current);
      lobbyHeartbeatTimeoutRef.current = null;
    }
    if (lobbyWsRef.current) {
      lobbyWsRef.current.onclose = null;
      lobbyWsRef.current.close();
      lobbyWsRef.current = null;
    }
  }, [cancelLobbyReconnect]);

  const refreshLobbyData = useCallback(async (): Promise<void> => {
    if (!tokenRef.current || !onlineUser) {
      setLobbyState(EMPTY_LOBBY_STATE);
      setSelectedLobbyUserId(null);
      return;
    }

    try {
      const lobbyResponse = await fetch(`${backendBaseUrl}/api/lobby`, {
        credentials: 'include',
        headers: authHeaders(),
      });

      if (!lobbyResponse.ok) {
        throw new Error(ERROR_MESSAGES.LOAD_LOBBY);
      }

      const lobbyData = await lobbyResponse.json() as OnlineLobbyPresenceResponse;
      const nextLobbyState = {
        ...lobbyData.lobby,
        activeGames: [],
      };

      setLobbyState(nextLobbyState);
      setSelectedLobbyUserId((currentSelection) => (
        currentSelection && nextLobbyState.users.some((user) => user.id === currentSelection && user.availability === 'available')
          ? currentSelection
          : null
      ));
    } catch (error) {
      setOnlineError(error instanceof Error ? error.message : ERROR_MESSAGES.LOAD_LOBBY);
    }
  }, [authHeaders, backendBaseUrl, onlineUser]);

  const connectRoomSocket = useCallback((roomId: string): void => {
    closeRoomSocket();

    const token = tokenRef.current;
    const tokenQuery = token ? `?token=${encodeURIComponent(token)}` : '';
    const socket = new WebSocket(`${wsBaseUrl}/ws/${encodeURIComponent(roomId)}${tokenQuery}`);
    wsRef.current = socket;

    const resetHeartbeatTimeout = () => {
      if (heartbeatTimeoutRef.current) clearTimeout(heartbeatTimeoutRef.current);
      heartbeatTimeoutRef.current = setTimeout(() => {
        if (wsRef.current === socket) {
          wsRef.current.close();
        }
      }, SOCKET_HEARTBEAT_TIMEOUT_MS);
    };

    socket.onopen = () => {
      if (wsRef.current !== socket) return;
      reconnectAttemptsRef.current = 0;
      setWsConnected(true);
      setOnlineError(null);
      setOnlineStatus(`Connected to room ${roomId}.`);
      resetHeartbeatTimeout();
    };

    socket.onclose = () => {
      if (wsRef.current !== socket) return;
      wsRef.current = null;
      setWsConnected(false);
      if (activeRoomIdRef.current !== roomId) return;
      if (reconnectAttemptsRef.current < 5) {
        const delay = 1000 * Math.pow(2, reconnectAttemptsRef.current);
        reconnectAttemptsRef.current += 1;
        setOnlineStatus(`Reconnecting... (${reconnectAttemptsRef.current}/5)`);
        reconnectTimeoutRef.current = setTimeout(() => {
          if (activeRoomIdRef.current === roomId) {
            connectSocketRef.current(roomId);
          }
        }, delay);
      } else {
        setOnlineStatus('Disconnected from room.');
        setOnlineError('Connection lost. Please rejoin the room.');
      }
    };

    socket.onerror = () => {
      // Close handler manages recovery.
    };

    socket.onmessage = (event: MessageEvent<string>) => {
      if (wsRef.current !== socket) return;
      let message: OnlineRoomServerMessage;

      try {
        message = JSON.parse(event.data) as OnlineRoomServerMessage;
      } catch {
        setOnlineError(ERROR_MESSAGES.MALFORMED_MESSAGE);
        return;
      }

      if (message.type === 'ping') {
        if (socket.readyState === WebSocket.OPEN) {
          const pong: OnlineRoomClientMessage = { type: 'pong' };
          socket.send(JSON.stringify(pong));
        }
        resetHeartbeatTimeout();
        return;
      }

      resetHeartbeatTimeout();

      if (message.type === 'state_update') {
        const wasInSetup = gamePhaseRef.current === 'setup';
        applyOnlineSnapshot(message.snapshot);
        if (wasInSetup && message.snapshot.gamePhase === 'playing') {
          setOnlineStatus('Game started — both setups are now revealed!');
          setBlindSetupSubmitted(false);
          setBlindOpponentSubmitted(false);
        }
       } else if (message.type === 'room_reset') {
        onlineRoleRef.current = message.room.role;
        setOnlineRoom(message.room);
        setOnlineRoomCode(message.room.roomId);
        setRoomPresence(null);
        setOnlineError(null);
        setGameStartedSignal(0);
        if (message.config.gameVariation === 'chess960blind') {
          // Blind Chess960: back to the secret-setup screen. Each player keeps
          // their previous arrangement (editable) and re-submits to start.
          prepareBlindSetup(message.config);
          setBlindSetupSubmitted(false);
          setBlindOpponentSubmitted(false);
          setOnlineStatus('Rematch — submit your setup to start (your previous arrangement is kept).');
        } else {
          startOnlineGame(message.config);
          if (message.requestedByUserId === onlineUser?.id) {
            setOnlineStatus('Rematch started.');
          } else {
            setOnlineStatus('Rematch started. Preparing the new board...');
          }
        }
      } else if (message.type === 'blind_setup_status') {
        const myRole = onlineRoleRef.current;
        setBlindSetupSubmitted(myRole === 'white' ? message.whiteSubmitted : message.blackSubmitted);
        setBlindOpponentSubmitted(myRole === 'white' ? message.blackSubmitted : message.whiteSubmitted);
      } else if (message.type === 'presence') {
        setRoomPresence(message.presence);
        setOnlineStatus(`Room presence • White: ${message.presence.whiteConnected ? 'online' : 'offline'} • Black: ${message.presence.blackConnected ? 'online' : 'offline'}`);
        setOnlineRoom((prev) => prev ? {
          ...prev,
          whiteName: message.presence.whiteName ?? prev.whiteName,
          blackName: message.presence.blackName ?? prev.blackName,
        } : prev);
      } else if (message.type === 'game_started') {
        // Use stable setter — avoids stale closure; useEffect calls forceTimerStart with fresh state
        setGameStartedSignal((prev) => prev + 1);
      } else if (message.type === 'room_closed') {
        const nextEntryMode = onlineRoom?.metadata?.source === 'private_room' ? 'private' : 'lobby';
        handleRoomClosedLocally(message.message, nextEntryMode);
      } else if (message.type === 'error') {
        setOnlineError(message.message);
        // If our blind setup submission was rejected, allow resubmitting.
        setBlindSetupSubmitted(false);
      }
    };
  }, [applyOnlineSnapshot, closeRoomSocket, handleRoomClosedLocally, onlineRoom?.metadata?.source, onlineUser?.id, prepareBlindSetup, startOnlineGame, wsBaseUrl]);

  connectSocketRef.current = connectRoomSocket;

  const connectLobbySocket = useCallback((): void => {
    if (!tokenRef.current || !onlineUser || gameMode !== 'online') return;

    closeLobbySocket();

    const tokenQuery = `?token=${encodeURIComponent(tokenRef.current)}`;
    const socket = new WebSocket(`${wsBaseUrl}/ws/lobby${tokenQuery}`);
    lobbyWsRef.current = socket;

    const resetHeartbeatTimeout = () => {
      if (lobbyHeartbeatTimeoutRef.current) clearTimeout(lobbyHeartbeatTimeoutRef.current);
      lobbyHeartbeatTimeoutRef.current = setTimeout(() => {
        if (lobbyWsRef.current === socket) {
          lobbyWsRef.current.close();
        }
      }, SOCKET_HEARTBEAT_TIMEOUT_MS);
    };

    socket.onopen = () => {
      if (lobbyWsRef.current !== socket) return;
      lobbyReconnectAttemptsRef.current = 0;
      void refreshLobbyData();
      resetHeartbeatTimeout();
    };

    socket.onclose = () => {
      if (lobbyWsRef.current !== socket) return;
      lobbyWsRef.current = null;
      if (gameModeRef.current !== 'online' || !onlineUserRef.current) return;
      if (lobbyReconnectAttemptsRef.current < 5) {
        const delay = 1000 * Math.pow(2, lobbyReconnectAttemptsRef.current);
        lobbyReconnectAttemptsRef.current += 1;
        lobbyReconnectTimeoutRef.current = setTimeout(() => {
          if (gameModeRef.current === 'online' && onlineUserRef.current) {
            connectLobbySocketRef.current();
          }
        }, delay);
      }
    };

    socket.onerror = () => {
      // Close handler manages recovery.
    };

    socket.onmessage = (event: MessageEvent<string>) => {
      if (lobbyWsRef.current !== socket) return;
      let message: OnlineLobbyServerMessage;

      try {
        message = JSON.parse(event.data) as OnlineLobbyServerMessage;
      } catch {
        setOnlineError('Received malformed lobby update.');
        return;
      }

      if (!message) return;

      if ((message as { type: string }).type === 'ping') {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type: 'pong' }));
        }
        resetHeartbeatTimeout();
        return;
      }

      if (message.type === 'lobby_state') {
        setLobbyState(message.lobby);
      } else if (message.type === 'invite_updated') {
        const invite = message.invite;
        setLobbyState((prev) => ({
          ...prev,
          incomingInvites: invite.status === 'pending' && invite.invitee.id === onlineUser?.id ? [invite] : [],
          outgoingInvites: invite.status === 'pending' && invite.inviter.id === onlineUser?.id ? [invite] : [],
        }));
      } else if (message.type === 'presence_updated') {
        const user = message.user;
        if (user.id === onlineUser?.id) {
          setLobbyState((prev) => ({ ...prev, self: user }));
        } else {
          setLobbyState((prev) => {
            const index = prev.users.findIndex((u) => u.id === user.id);
            let nextUsers;
            if (index >= 0) {
              nextUsers = [...prev.users];
              nextUsers[index] = user;
            } else {
              nextUsers = [...prev.users, user];
              nextUsers.sort((a, b) => a.name.localeCompare(b.name));
            }
            return { ...prev, users: nextUsers };
          });
        }
      }
    };
  }, [closeLobbySocket, gameMode, onlineUser, refreshLobbyData, wsBaseUrl]);

  connectLobbySocketRef.current = connectLobbySocket;

  const refreshOnlineSession = useCallback(async (): Promise<void> => {
    try {
      const response = await fetch(`${backendBaseUrl}/api/me`, {
        credentials: 'include',
        headers: authHeaders(),
      });

      if (!response.ok) {
        setOnlineUser(null);
        setLobbyState(EMPTY_LOBBY_STATE);
        clearToken();
        tokenRef.current = null;
        closeLobbySocket();
        return;
      }

      const data = await response.json() as SessionResponse;
      setOnlineUser(data.user);
      if (!data.user) {
        clearToken();
        tokenRef.current = null;
        setLobbyState(EMPTY_LOBBY_STATE);
        closeLobbySocket();
      }
    } catch {
      setOnlineUser(null);
      setLobbyState(EMPTY_LOBBY_STATE);
    }
  }, [authHeaders, backendBaseUrl, closeLobbySocket]);

  useEffect(() => {
    void refreshOnlineSession();
  }, [refreshOnlineSession]);

  const syncConfigIntoGame = useCallback((config: OnlineRoomConfig) => {
    setGameVariation(config.gameVariation);
    setPlayerPieces(config.playerPieces);
    setPromotionRule(config.promotionRule);
    setChess960PositionId(config.chess960PositionId ?? null);
    if (config.timeControl !== undefined) {
      setTimeControl(config.timeControl);
    }
  }, [setGameVariation, setPlayerPieces, setPromotionRule, setTimeControl]);

  const enterParticipantRoom = useCallback((room: OnlineRoomSession, config: OnlineRoomConfig, snapshot: OnlineGameSnapshot | null, status: string) => {
    setGameMode('online');
    syncConfigIntoGame(config);
    setOnlineRoom(room);
    onlineRoleRef.current = room.role;
    activeRoomIdRef.current = room.roomId;
    setOnlineRoomCode(room.roomId);
    setOnlineStatus(status);
    setOnlineError(null);
    setRoomPresence(null);
    autoJoinRoomRef.current = room.roomId;
    storeRoom({ roomCode: room.roomId, role: room.role });
    connectRoomSocket(room.roomId);
    if (snapshot) {
      applyOnlineSnapshot(snapshot);
    } else if (config.gameVariation === 'chess960blind') {
      // Blind Chess960: stay in the secret-setup phase. The server starts the
      // game (and reveals the board) once both players have submitted.
      prepareBlindSetup(config);
      setBlindSetupSubmitted(false);
      setBlindOpponentSubmitted(false);
      const sharedPiece = config.playerPieces.white ?? 'Q';
      setBlindRank((previous) => (
        previous && validateBlindBackRank(previous, sharedPiece) === null
          ? previous
          : randomChess960BackRank(sharedPiece)
      ));
    } else {
      startOnlineGame(config);
    }
  }, [applyOnlineSnapshot, connectRoomSocket, prepareBlindSetup, startOnlineGame, syncConfigIntoGame]);

  const joinRoomById = useCallback(async (roomId: string, statusMessage: string): Promise<void> => {
    const response = await fetch(`${backendBaseUrl}/api/rooms/${encodeURIComponent(roomId)}/join`, {
      method: 'POST',
      credentials: 'include',
      headers: authHeaders(),
    });

    if (!response.ok) {
      throw new Error(ERROR_MESSAGES.JOIN_ROOM);
    }

    const data = await response.json() as RoomJoinResponse;
    enterParticipantRoom({
      roomId: data.roomId,
      role: data.role,
      whiteName: data.whiteName,
      blackName: data.blackName,
      metadata: data.metadata,
    }, data.config, data.snapshot, statusMessage);
  }, [authHeaders, backendBaseUrl, enterParticipantRoom]);

  useEffect(() => {
    if (!onlineUser || onlineRoom) return;
    if (pendingUrlJoinRef.current) return;
    const persisted = getStoredRoom();
    if (!persisted) return;

    const rejoin = async () => {
      try {
        await joinRoomById(persisted.roomCode, `Rejoined room ${persisted.roomCode}.`);
      } catch {
        clearStoredRoom();
      }
    };

    void rejoin();
  }, [joinRoomById, onlineRoom, onlineUser]);

  useEffect(() => {
    const pendingCode = pendingUrlJoinRef.current;
    if (!pendingCode || !onlineUser || onlineRoom) return;
    pendingUrlJoinRef.current = null;

    const doJoin = async () => {
      setOnlineBusy(true);
      setOnlineError(null);
      try {
        await joinRoomById(pendingCode, `Joined room ${pendingCode}.`);
      } catch (error) {
        setOnlineError(error instanceof Error ? error.message : ERROR_MESSAGES.JOIN_ROOM);
      } finally {
        setOnlineBusy(false);
      }
    };

    void doJoin();
  }, [joinRoomById, onlineRoom, onlineUser]);

  useEffect(() => {
    return () => {
      closeRoomSocket();
      closeLobbySocket();
    };
  }, [closeLobbySocket, closeRoomSocket]);

  useEffect(() => {
    if (gameMode === 'online' && onlineUser) {
      connectLobbySocket();
      return;
    }

    closeLobbySocket();
    setLobbyState(EMPTY_LOBBY_STATE);
    setSelectedLobbyUserId(null);
  }, [closeLobbySocket, connectLobbySocket, gameMode, onlineUser, refreshLobbyData]);

  useEffect(() => {
    const pendingInvite = lobbyState.incomingInvites.find((invite) => invite.status === 'pending') ?? null;

    if (!pendingInvite) {
      lastNotifiedInviteIdRef.current = null;
      return;
    }

    if (pendingInvite.id !== lastNotifiedInviteIdRef.current) {
      playLobbyInviteSound();
      lastNotifiedInviteIdRef.current = pendingInvite.id;
      setOnlineStatus(`New lobby invite from ${pendingInvite.inviter.name}.`);
    }
  }, [lobbyState.incomingInvites]);

  useEffect(() => {
    if (gameMode !== 'online' || !onlineUser || onlineRoom) return;

    const refreshOnFocus = () => {
      void refreshLobbyData();
    };

    const refreshOnVisible = () => {
      if (document.visibilityState === 'visible') {
        void refreshLobbyData();
      }
    };

    window.addEventListener('focus', refreshOnFocus);
    document.addEventListener('visibilitychange', refreshOnVisible);

    return () => {
      window.removeEventListener('focus', refreshOnFocus);
      document.removeEventListener('visibilitychange', refreshOnVisible);
    };
  }, [gameMode, onlineRoom, onlineUser, refreshLobbyData]);

  useEffect(() => {
    if (gameMode === 'online' && onlineRoom) {
      setPlayerControlColor(onlineRoom.role);
    } else {
      setPlayerControlColor(null);
    }
  }, [gameMode, onlineRoom, setPlayerControlColor]);

  useEffect(() => {
    if (gameMode === 'local' && onlineRoom) {
      closeRoomSocket();
      clearStoredRoom();
      setOnlineRoom(null);
      setRoomPresence(null);
      setOnlineStatus('');
      setOnlineError(null);
      setOnlineRoomCode('');
      autoJoinRoomRef.current = null;
      activeRoomIdRef.current = null;
    }
  }, [closeRoomSocket, gameMode, onlineRoom]);

  useEffect(() => {
    if (gameMode !== 'online' || !onlineRoom) return;

    const ensureRoomSocket = () => {
      const readyState = wsRef.current?.readyState;
      if (readyState === WebSocket.OPEN || readyState === WebSocket.CONNECTING) return;
      connectSocketRef.current(onlineRoom.roomId);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        ensureRoomSocket();
      }
    };

    window.addEventListener('focus', ensureRoomSocket);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', ensureRoomSocket);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [gameMode, onlineRoom]);

  useEffect(() => {
    if (gameMode !== 'online') return;
    if (!onlineRoom) return;
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

    // Blind Chess960: during the secret-setup phase the server owns all state
    // and there is nothing to sync yet.
    if (gameVariation === 'chess960blind' && gamePhase === 'setup') return;

    // If we just applied a server resync, skip this send — React state hasn't fully
    // flushed yet so getOnlineSnapshot() would return stale data and cause another conflict.
    if (pendingResyncRef.current) {
      pendingResyncRef.current = false;
      return;
    }

    const snapshot = getOnlineSnapshot();
    const serialized = JSON.stringify(snapshot);
    if (serialized === lastSentSnapshotRef.current) return;

    snapshotVersionRef.current += 1;
    snapshot.version = snapshotVersionRef.current;

    const finalSerialized = JSON.stringify(snapshot);
    lastSentSnapshotRef.current = finalSerialized;

    const payload: OnlineRoomClientMessage = {
      type: 'sync_state',
      snapshot,
    };

    wsRef.current.send(JSON.stringify(payload));
  }, [board, currentPlayer, drawAgreed, drawOfferedBy, gameHistory, gameMode, gamePhase, gameVariation, getOnlineSnapshot, lastSentSnapshotRef, onlineRoom, pendingResyncRef, snapshotVersionRef, timeoutWinner, wsConnected]);

  // React to game_started signal with fresh state (avoids stale closure in onmessage handler)
  useEffect(() => {
    if (gameStartedSignal === 0) return;
    forceTimerStart();
  }, [gameStartedSignal, forceTimerStart]);

  // Blind Chess960 setup phase: poll the server for the authoritative state.
  // Covers missed one-shot broadcasts (cross-isolate/region) and stale local
  // snapshot bookkeeping — the server responds with the game snapshot once
  // both setups are in, which auto-starts the game on this client.
  useEffect(() => {
    if (gameMode !== 'online' || !onlineRoom || gameVariation !== 'chess960blind' || gamePhase !== 'setup') {
      return;
    }

    const requestState = () => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        const payload: OnlineRoomClientMessage = { type: 'request_state' };
        wsRef.current.send(JSON.stringify(payload));
      }
    };

    requestState();
    const intervalId = window.setInterval(requestState, 1500);
    return () => window.clearInterval(intervalId);
  }, [gameMode, gamePhase, gameVariation, onlineRoom, wsConnected]);

  // Safety net: keep the blind setup editor's rank consistent with the room's
  // shared major piece. Covers every entry path (create, join, invite accept,
  // rematch) — if the pre-filled rank contains a different piece, regenerate
  // it so a "must contain exactly one <piece>" warning can never stick.
  useEffect(() => {
    if (gameMode !== 'online' || !onlineRoom || gameVariation !== 'chess960blind' || gamePhase !== 'setup') {
      return;
    }
    const sharedPiece = sharedPieceForBlind(playerPieces);
    setBlindRank((previous) => (
      previous && validateBlindBackRank(previous, sharedPiece) === null
        ? previous
        : randomChess960BackRank(sharedPiece)
    ));
  }, [gameMode, gamePhase, gameVariation, onlineRoom, playerPieces]);

  useEffect(() => {
    if (gameMode !== 'online' || !onlineRoom || gamePhase !== 'setup') {
      return;
    }
    // Blind Chess960 games wait for the server-authored initial snapshot.
    if (gameVariation === 'chess960blind') {
      return;
    }

    startOnlineGame({
      gameVariation,
      playerPieces,
      promotionRule,
      chess960PositionId: chess960PositionId ?? undefined,
      timeControl: timeControl ?? undefined,
    });
  }, [
    chess960PositionId,
    gameMode,
    gamePhase,
    gameVariation,
    onlineRoom,
    playerPieces,
    promotionRule,
    startOnlineGame,
    timeControl,
  ]);

  useEffect(() => {
    if (gameMode !== 'online' || !onlineUser || onlineRoom) return;

    const activeRoomId = lobbyState.self?.availability === 'in_game' ? lobbyState.self.activeRoomId : null;

    if (!activeRoomId || autoJoinRoomRef.current === activeRoomId || onlineBusy) return;

    autoJoinRoomRef.current = activeRoomId;
    const connectAcceptedRoom = async () => {
      setOnlineBusy(true);
      setOnlineError(null);
      try {
        await joinRoomById(activeRoomId, 'Invite accepted. Connected to live game.');
      } catch (error) {
        autoJoinRoomRef.current = null;
        setOnlineError(error instanceof Error ? error.message : ERROR_MESSAGES.CONNECT_ACCEPTED_GAME);
      } finally {
        setOnlineBusy(false);
      }
    };

    void connectAcceptedRoom();
  }, [gameMode, joinRoomById, lobbyState, onlineBusy, onlineRoom, onlineUser]);

  const createOnlineRoom = async (chess960PositionId?: number | null): Promise<void> => {
    if (!onlineUser) {
      setOnlineError('Please sign in with GitHub, Google, Facebook, or Lichess before creating a room.');
      return;
    }

    const sharedBlindPiece = sharedPieceForBlind(playerPieces);
    const payload: OnlineRoomConfig = {
      gameVariation,
      playerPieces: gameVariation === 'chess960blind'
        ? { white: sharedBlindPiece, black: sharedBlindPiece }
        : playerPieces,
      promotionRule,
      timeControl: timeControl ?? undefined,
      chess960PositionId: chess960PositionId ?? undefined
    };

    if (!isConfigReady(payload)) {
      setOnlineError('Complete the game setup before creating a private room.');
      return;
    }

    setOnlineBusy(true);
    setOnlineError(null);

    try {
      const response = await fetch(`${backendBaseUrl}/api/rooms`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders(),
        },
        body: JSON.stringify({ config: payload }),
      });

      if (!response.ok) {
        throw new Error(ERROR_MESSAGES.CREATE_ROOM);
      }

      const data = await response.json() as RoomCreateResponse;
      setOnlineEntryMode('private');
      onlineRoleRef.current = data.role;
      setOnlineRoom({
        roomId: data.roomId,
        role: data.role,
        whiteName: data.whiteName,
        blackName: data.blackName,
        metadata: data.metadata,
      });
      setOnlineRoomCode(data.roomId);
      setOnlineStatus('Private room created. Share the invite link with your opponent.');
      setRoomPresence(null);
      setOnlineError(null);
      autoJoinRoomRef.current = data.roomId;
      storeRoom({ roomCode: data.roomId, role: data.role });
      connectRoomSocket(data.roomId);
      if (gameVariation === 'chess960blind') {
        // Blind Chess960: stay in the secret-setup phase; the game starts
        // automatically once both players have submitted their back rank.
        prepareBlindSetup(payload);
        setBlindSetupSubmitted(false);
        setBlindOpponentSubmitted(false);
        const sharedPiece = payload.playerPieces.white ?? 'Q';
        setBlindRank((previous) => (
          previous && validateBlindBackRank(previous, sharedPiece) === null
            ? previous
            : randomChess960BackRank(sharedPiece)
        ));
        setOnlineStatus('Private room created. Arrange your secret first-rank setup and submit it.');
      } else {
        startGame(timeControl, chess960PositionId);
      }
    } catch (error) {
      setOnlineError(error instanceof Error ? error.message : ERROR_MESSAGES.CREATE_ROOM);
    } finally {
      setOnlineBusy(false);
    }
  };

  const joinOnlineRoom = async (): Promise<void> => {
    if (!onlineUser) {
      setOnlineError('Please sign in with GitHub, Google, Facebook, or Lichess before joining a room.');
      return;
    }

    const normalizedCode = onlineRoomCode.trim().toUpperCase();
    if (!normalizedCode) {
      setOnlineError('Enter a room code first.');
      return;
    }

    setOnlineBusy(true);
    setOnlineError(null);

    try {
      setOnlineEntryMode('private');
      await joinRoomById(normalizedCode, `Joined room ${normalizedCode}.`);
    } catch (error) {
      setOnlineError(error instanceof Error ? error.message : ERROR_MESSAGES.JOIN_ROOM);
    } finally {
      setOnlineBusy(false);
    }
  };

  const leaveOnlineRoom = useCallback((): void => {
    const roomId = onlineRoom?.roomId ?? null;

    if (gameMode === 'online' && onlineRoom && gamePhase === 'playing') {
      const status = getGameStatus();
      if (status.status === 'ongoing') {
        const participantRole = onlineRoom.role;
        resignGame(participantRole);
        
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          const snapshot = getOnlineSnapshot();
          snapshot.resignedBy = participantRole;
          snapshotVersionRef.current += 1;
          snapshot.version = snapshotVersionRef.current;
          lastSentSnapshotRef.current = JSON.stringify(snapshot);
          
          const payload: OnlineRoomClientMessage = {
            type: 'sync_state',
            snapshot,
          };
          wsRef.current.send(JSON.stringify(payload));
        }
      }
    }

    closeRoomSocket();
    clearStoredRoom();
    setOnlineRoom(null);
    setRoomPresence(null);
    setLobbyState((previous) => previous.self ? {
      ...previous,
      self: {
        ...previous.self,
        availability: 'available',
        activeRoomId: null,
      },
      outgoingInvites: previous.outgoingInvites.filter((invite) => invite.roomId !== onlineRoom?.roomId),
      incomingInvites: previous.incomingInvites.filter((invite) => invite.roomId !== onlineRoom?.roomId),
    } : previous);
    setOnlineStatus('Left online room.');
    setOnlineError(null);
    setOnlineRoomCode('');
    autoJoinRoomRef.current = null;
    activeRoomIdRef.current = null;

    if (!roomId || !onlineUser) {
      void refreshLobbyData();
      return;
    }

    void (async () => {
      try {
        const response = await fetch(`${backendBaseUrl}/api/rooms/${encodeURIComponent(roomId)}/leave`, {
          method: 'POST',
          credentials: 'include',
          headers: authHeaders(),
        });

        if (!response.ok) {
          throw new Error(ERROR_MESSAGES.LEAVE_ROOM);
        }
      } catch (error) {
        setOnlineError(error instanceof Error ? error.message : ERROR_MESSAGES.LEAVE_ROOM);
      } finally {
        await refreshLobbyData();
      }
    })();
  }, [authHeaders, backendBaseUrl, closeRoomSocket, gameMode, gamePhase, getGameStatus, getOnlineSnapshot, lastSentSnapshotRef, onlineRoom, onlineUser, refreshLobbyData, resignGame, snapshotVersionRef]);

  const sendLobbyInvite = useCallback(async (chess960PositionId?: number | null): Promise<void> => {
    if (!onlineUser) {
      setOnlineError('Please sign in before sending a lobby invite.');
      return;
    }

    const sharedBlindPiece = sharedPieceForBlind(playerPieces);
    const payload: OnlineRoomConfig = {
      gameVariation,
      playerPieces: gameVariation === 'chess960blind'
        ? { white: sharedBlindPiece, black: sharedBlindPiece }
        : playerPieces,
      promotionRule,
      timeControl: timeControl ?? undefined,
      chess960PositionId: chess960PositionId ?? undefined
    };

    if (!selectedLobbyUserId) {
      setOnlineError('Choose an available player from the lobby first.');
      return;
    }

    if (!isConfigReady(payload)) {
      setOnlineError('Complete the game configuration before sending an invite.');
      return;
    }

    setOnlineBusy(true);
    setOnlineError(null);

    try {
      const requestBody: OnlineCreateInviteRequest = {
        inviteeUserId: selectedLobbyUserId,
        config: payload,
        colorPreference: inviteColorPreference,
      };

      const response = await fetch(`${backendBaseUrl}/api/lobby/invites`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders(),
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(ERROR_MESSAGES.SEND_INVITE);
      }

      await response.json() as OnlineInviteResponse;
      setOnlineStatus('Invite sent. Waiting for your opponent to respond.');
      await refreshLobbyData();
    } catch (error) {
      setOnlineError(error instanceof Error ? error.message : ERROR_MESSAGES.SEND_INVITE);
    } finally {
      setOnlineBusy(false);
    }
  }, [authHeaders, backendBaseUrl, gameVariation, inviteColorPreference, onlineUser, playerPieces, promotionRule, refreshLobbyData, selectedLobbyUserId, timeControl]);

  const acceptLobbyInvite = useCallback(async (inviteId: string): Promise<void> => {
    if (!onlineUser) return;

    setOnlineBusy(true);
    setOnlineError(null);

    try {
      const response = await fetch(`${backendBaseUrl}/api/lobby/invites/${encodeURIComponent(inviteId)}/accept`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(),
      });

      if (!response.ok) {
        throw new Error(ERROR_MESSAGES.ACCEPT_INVITE);
      }

      const data = await response.json() as OnlineAcceptInviteResponse;
      setOnlineEntryMode('lobby');
      enterParticipantRoom(data.room, data.config, data.snapshot, `Accepted invite from ${data.invite.inviter.name}.`);
      await refreshLobbyData();
    } catch (error) {
      setOnlineError(error instanceof Error ? error.message : ERROR_MESSAGES.ACCEPT_INVITE);
    } finally {
      setOnlineBusy(false);
    }
  }, [authHeaders, backendBaseUrl, enterParticipantRoom, onlineUser, refreshLobbyData]);

  const resolveLobbyInvite = useCallback(async (inviteId: string, action: 'refuse' | 'cancel', successMessage: string): Promise<void> => {
    setOnlineBusy(true);
    setOnlineError(null);

    try {
      const response = await fetch(`${backendBaseUrl}/api/lobby/invites/${encodeURIComponent(inviteId)}/${action}`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to ${action} invite.`);
      }

      await response.json() as OnlineInviteResponse;
      setOnlineStatus(successMessage);
      await refreshLobbyData();
    } catch (error) {
      const errorMsg = action === 'cancel' ? ERROR_MESSAGES.CANCEL_INVITE : ERROR_MESSAGES.REFUSE_INVITE;
      setOnlineError(error instanceof Error ? error.message : errorMsg);
    } finally {
      setOnlineBusy(false);
    }
  }, [authHeaders, backendBaseUrl, refreshLobbyData]);

  const submitBlindSetup = useCallback((): void => {
    if (!onlineRoom || gameVariation !== 'chess960blind') return;

    const sharedPiece = playerPieces.white ?? 'Q';
    if (!blindRank || validateBlindBackRank(blindRank, sharedPiece) !== null) {
      setOnlineError('Your arrangement is not a valid Chess960 back rank.');
      return;
    }

    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      setOnlineError('Room connection lost. Reconnect before submitting your setup.');
      return;
    }

    const payload: OnlineRoomClientMessage = {
      type: 'submit_blind_setup',
      backRank: blindRank
    };
    wsRef.current.send(JSON.stringify(payload));
    setBlindSetupSubmitted(true);
    setOnlineError(null);
    setOnlineStatus('Setup submitted. Waiting for your opponent…');
  }, [blindRank, gameVariation, onlineRoom, playerPieces]);

  const handleForceTimerStart = useCallback(() => {
    if (gameMode === 'online' && onlineRoom && onlineRoom.role === 'black') {
      // Send start_game to server; server will broadcast game_started to both players
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        const payload: OnlineRoomClientMessage = { type: 'start_game' };
        wsRef.current.send(JSON.stringify(payload));
      }
    } else {
      // Offline / local mode: start directly
      forceTimerStart();
    }
  }, [forceTimerStart, gameMode, onlineRoom]);

  const handleStartGame = async (chess960PositionId?: number | null) => {
    if (gameMode === 'online' && !onlineRoom) {
      await createOnlineRoom(chess960PositionId);
    } else {
      // Pass current timeControl state and chess960PositionId to avoid stale state issues
      startGame(timeControl, chess960PositionId);
    }
  };

  const requestOnlineRematch = useCallback(() => {
    if (!onlineRoom) {
      resetGame();
      return;
    }

    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      setOnlineError('Room connection lost. Reconnect before starting a rematch.');
      return;
    }

    setOnlineError(null);
    setOnlineStatus('Starting rematch...');
    const payload: OnlineRoomClientMessage = { type: 'request_rematch' };
    wsRef.current.send(JSON.stringify(payload));
  }, [onlineRoom, resetGame]);

  const handleResetOrLeave = useCallback(() => {
    if (gameMode === 'online' && onlineRoom) {
      requestOnlineRematch();
      return;
    }

    resetGame();
  }, [gameMode, onlineRoom, requestOnlineRematch, resetGame]);



  const handleLeaveOnlineRoom = useCallback(() => {
    if (!onlineRoom) {
      resetGame();
      return;
    }

    const nextEntryMode = onlineRoom.metadata?.source === 'private_room' ? 'private' : 'lobby';
    leaveOnlineRoom();
    resetGame();
    setGameMode('online');
    setOnlineEntryMode(nextEntryMode);
  }, [leaveOnlineRoom, onlineRoom, resetGame]);

  const copyInviteLink = () => {
    if (!onlineRoom) return;
    const url = new URL(window.location.href);
    url.searchParams.set('room', onlineRoom.roomId);
    void navigator.clipboard.writeText(url.toString());
    setOnlineStatus('Invite link copied to clipboard!');
  };

  const browserSessionGameRecord = useMemo<BrowserSessionGameRecord>(() => {
    const players = gameMode === 'online'
      ? {
        white: onlineRoom?.whiteName ?? 'White',
        black: onlineRoom?.blackName ?? 'Black',
      }
      : {
        white: 'White',
        black: 'Black',
      };

    const startingBackRanks = gameVariation === 'chess960blind' && gamePhase !== 'setup'
      ? {
        // Blind Chess960: report the actual (asymmetric) revealed back ranks.
        white: board[7].map((square) => square?.piece ?? '?').join(''),
        black: board[0].map((square) => square?.piece ?? '?').join(''),
      }
      : buildStartingBackRanks(gameVariation, playerPieces, chess960PositionId);
    const hasNonStandardStart = startingBackRanks.white !== 'RNBQKBNR' || startingBackRanks.black !== 'RNBQKBNR';
    const moves = Array.from({ length: Math.ceil(gameHistory.length / 2) }, (_, moveNumber) => {
      const whiteMove = gameHistory[moveNumber * 2]?.notation ?? '';
      const blackMove = gameHistory[moveNumber * 2 + 1]?.notation;

      return blackMove
        ? `${moveNumber + 1}. ${whiteMove} ${blackMove}`
        : `${moveNumber + 1}. ${whiteMove}`;
    });
    const lines = [
      `Game Type: ${gameMode === 'online' ? 'Online' : 'Over the board'}`,
      `Players: White - ${players.white}; Black - ${players.black}`,
      `Variation: ${formatVariationLabel(gameVariation)}`,
      `Promotion Rule: ${promotionRule}`,
    ];

    if (hasNonStandardStart) {
      lines.push('Start Position:');
      lines.push(`White back rank: ${startingBackRanks.white}`);
      lines.push(`Black back rank: ${startingBackRanks.black}`);
      if (gameVariation.includes('960') && gameVariation !== 'chess960blind') {
        lines.push(`Chess960 position: ${chess960PositionId ?? 'random'}`);
      }
      if (gameVariation === 'chess960blind') {
        lines.push('Chess960 position: blind — each player chose their own secret arrangement');
      }
    }

    lines.push('Moves:');
    lines.push(formatMovesForExport(gameHistory));

    return {
      gameType: gameMode === 'online' ? 'online' : 'over-the-board',
      players,
      variation: formatVariationLabel(gameVariation),
      promotionRule,
      startPosition: {
        whiteBackRank: startingBackRanks.white,
        blackBackRank: startingBackRanks.black,
        chess960PositionId,
        isNonStandard: hasNonStandardStart,
      },
      moves,
      text: lines.join('\n'),
      updatedAt: Date.now(),
    };
  }, [board, chess960PositionId, gameHistory, gameMode, gamePhase, gameVariation, onlineRoom?.blackName, onlineRoom?.whiteName, playerPieces, promotionRule]);

  useEffect(() => {
    if (gamePhase === 'setup') {
      clearStoredGameRecord();
      return;
    }

    storeGameRecord(browserSessionGameRecord);
  }, [browserSessionGameRecord, gamePhase]);

  const handleCopyGameRecord = useCallback(() => {
    const currentRecord = getStoredGameRecord() ?? browserSessionGameRecord;

    void navigator.clipboard.writeText(currentRecord.text)
      .then(() => {
        setGameRecordFeedback('Game record copied to clipboard.');
      })
      .catch(() => {
        setGameRecordFeedback('Failed to copy game record.');
      });
  }, [browserSessionGameRecord]);

  const handleDownloadGameRecord = useCallback(() => {
    const currentRecord = getStoredGameRecord() ?? browserSessionGameRecord;
    const blob = new Blob([currentRecord.text], { type: 'text/plain;charset=utf-8' });
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = `${gameVariation}-${gameMode}-moves.txt`;
    anchor.click();
    URL.revokeObjectURL(objectUrl);
    setGameRecordFeedback('Game record download started.');
  }, [browserSessionGameRecord, gameMode, gameVariation]);

  const gameStatus = getGameStatus();
  const participantRole: Color | null = onlineRoom?.role ?? null;
  const canQueueDrawOffer = gameMode === 'online'
    && participantRole !== null
    && participantRole === currentPlayer
    && !drawOfferedBy
    && !drawAgreed
    && gameStatus.status === 'ongoing'
    && gamePhase === 'playing'
    && wsConnected;
  const hasIncomingDrawOffer = gameMode === 'online'
    && participantRole !== null
    && drawOfferedBy !== null
    && drawOfferedBy !== participantRole
    && !drawAgreed
    && gameStatus.status === 'ongoing';
  const hasOutgoingDrawOffer = gameMode === 'online'
    && participantRole !== null
    && drawOfferedBy === participantRole
    && !drawAgreed
    && gameStatus.status === 'ongoing';
  const canShowPrivateInviteBanner = gameMode === 'online'
    && onlineRoom?.role === 'white'
    && !onlineRoom.blackName
    && onlineRoom.metadata?.source !== 'lobby_invite';
  const canForceTimerStart = gamePhase === 'playing'
    && gameHistory.length === 0
    && turnStartMs === null
    && (gameMode !== 'online' || participantRole === 'black');

  // ── Screen reader announcements (polite live region) ───────────────────────
  const [liveAnnouncement, setLiveAnnouncement] = useState('');
  const lastAnnouncementKeyRef = useRef<string>('');

  useEffect(() => {
    if (gamePhase !== 'playing') return;

    const colorLabel = (color: Color): string => (color === 'white' ? 'White' : 'Black');
    const lastMove = gameHistory[gameHistory.length - 1] ?? null;
    const key = [
      gameStatus.status,
      gameStatus.winner ?? 'none',
      gameHistory.length,
      currentPlayer,
      drawOfferedBy ?? 'none',
      drawAgreed ? 'agreed' : 'open'
    ].join('|');

    if (key === lastAnnouncementKeyRef.current) return;

    let text = '';
    if (gameStatus.status !== 'ongoing') {
      const winner = gameStatus.winner ? `${colorLabel(gameStatus.winner)} wins` : 'The game is a draw';
      switch (gameStatus.status) {
        case 'checkmate': text = `Checkmate. ${winner}.`; break;
        case 'stalemate': text = 'Stalemate. The game is a draw.'; break;
        case 'resignation': text = `Resignation. ${winner}.`; break;
        case 'timeout': text = `Time out. ${winner}.`; break;
        case 'draw_agreement': text = 'Draw agreed. The game is a draw.'; break;
        default: text = '';
      }
    } else if (hasIncomingDrawOffer) {
      text = `${colorLabel(drawOfferedBy ?? 'white')} offers a draw.`;
    } else if (lastMove) {
      const mover = colorLabel(lastMove.piece.color);
      if (lastMove.isCastling) {
        text = `${mover} castles.`;
      } else {
        const pieceName = PIECE_NAMES[lastMove.piece.piece].toLowerCase();
        text = `${mover} moves ${pieceName} from ${positionToAlgebraic(lastMove.from)} to ${positionToAlgebraic(lastMove.to)}.`;
      }
      if (lastMove.isCheckmate) {
        text += ` Checkmate. ${colorLabel(lastMove.piece.color)} wins.`;
      } else if (lastMove.isCheck) {
        text += ` Check. ${colorLabel(currentPlayer)} to move.`;
      } else {
        text += ` ${colorLabel(currentPlayer)} to move.`;
      }
    } else {
      text = `${colorLabel(currentPlayer)} to move.`;
    }

    if (!text) return;
    lastAnnouncementKeyRef.current = key;
    setLiveAnnouncement(text);
  }, [gamePhase, gameStatus.status, gameStatus.winner, gameHistory, currentPlayer, drawOfferedBy, drawAgreed, hasIncomingDrawOffer]);

  return (
    <div className="min-h-screen bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-slate-950 to-black flex flex-col items-center justify-start p-3 xs:p-4 sm:p-5 md:p-6 lg:p-8 font-sans text-slate-200 selection:bg-cyan-600 selection:text-white">

      {/* Visually hidden live region: announces moves, checks, and results to screen readers */}
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {liveAnnouncement}
      </div>

      <header className="mt-2 mb-4 xs:mb-5 sm:mb-7 text-center w-full max-w-5xl">
        <p className="inline-flex items-center rounded-full border border-cyan-400/30 bg-cyan-500/10 px-3 py-1 text-xs xs:text-sm text-cyan-200 mb-2 xs:mb-3">
          Strategy • Variants • Chess960
        </p>
        <h1 className="text-2xl xs:text-3xl sm:text-5xl md:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-cyan-300 to-orange-400 mb-1 xs:mb-2 sm:mb-3 tracking-tight">
          Chess960x32
        </h1>
        <p className="text-base xs:text-lg sm:text-lg md:text-xl text-slate-300 font-light tracking-wide">
          The Ultimate Chess Evolution
        </p>
      </header>

      <main className="w-full max-w-[1680px] mx-auto flex flex-col items-center">
        {gamePhase === 'setup' && (
          <div className="w-full max-w-6xl animate-in fade-in slide-in-from-bottom-8 duration-700">
            {gameMode === 'online' && onlineRoom && gameVariation === 'chess960blind' ? (
              <>
                {(onlineStatus || onlineError) && (
                  <div className="mb-4 space-y-2 text-sm">
                    {onlineStatus && (
                      <div className="rounded-lg border border-cyan-400/20 bg-cyan-500/10 p-3 text-cyan-100">
                        {onlineStatus}
                      </div>
                    )}
                    {onlineError && (
                      <div className="rounded-lg border border-rose-400/30 bg-rose-500/10 p-3 text-rose-200">
                        {onlineError}
                      </div>
                    )}
                  </div>
                )}
                <BlindSetupScreen
                  backRank={blindRank}
                  onBackRankChange={setBlindRank}
                  sharedMajorPiece={playerPieces.white ?? 'Q'}
                  ownColor={onlineRoom.role}
                  ownName={onlineUser?.name ?? 'You'}
                  opponentName={onlineRoom.role === 'white' ? onlineRoom.blackName : onlineRoom.whiteName}
                  submitted={blindSetupSubmitted}
                  opponentSubmitted={blindOpponentSubmitted}
                  onSubmit={submitBlindSetup}
                  onLeaveRoom={handleLeaveOnlineRoom}
                  onCopyInviteLink={canShowPrivateInviteBanner ? copyInviteLink : undefined}
                />
              </>
            ) : (
              <GameSetupWizard
              gameVariation={gameVariation}
              setGameVariation={setGameVariation}
              chess960PositionId={chess960PositionId}
              setChess960PositionId={setChess960PositionId}
              playerPieces={playerPieces}
              setPlayerPieces={setPlayerPieces}
              promotionRule={promotionRule}
              setPromotionRule={setPromotionRule}
              gameMode={gameMode}
              setGameMode={setGameMode}
              onlineUser={onlineUser}
              onlineRoomId={onlineRoom?.roomId ?? null}
              onlineRole={onlineRoom?.role ?? null}
              onlineRoomCode={onlineRoomCode}
              setOnlineRoomCode={setOnlineRoomCode}
              onlineEntryMode={onlineEntryMode}
              setOnlineEntryMode={setOnlineEntryMode}
              lobbyState={lobbyState}
              inviteColorPreference={inviteColorPreference}
              setInviteColorPreference={setInviteColorPreference}
              selectedLobbyUserId={selectedLobbyUserId}
              setSelectedLobbyUserId={setSelectedLobbyUserId}
              onlineBusy={onlineBusy}
              onlineStatus={onlineStatus}
              onlineError={onlineError}
              githubLoginUrl={githubLoginUrl}
              googleLoginUrl={googleLoginUrl}
              facebookLoginUrl={facebookLoginUrl}
              lichessLoginUrl={lichessLoginUrl}
              logoutUrl={logoutUrl}
              joinOnlineRoom={joinOnlineRoom}
              leaveOnlineRoom={leaveOnlineRoom}
              sendLobbyInvite={sendLobbyInvite}
              acceptLobbyInvite={acceptLobbyInvite}
              refuseLobbyInvite={(inviteId) => void resolveLobbyInvite(inviteId, 'refuse', 'Invite refused.')}
              cancelLobbyInvite={(inviteId) => void resolveLobbyInvite(inviteId, 'cancel', 'Invite cancelled.')}
              startGame={handleStartGame}
               timeControl={timeControl}
               setTimeControl={setTimeControl}
               backendBaseUrl={backendBaseUrl}
               authHeaders={authHeaders}
             />
            )}
          </div>
        )}

        {gamePhase !== 'setup' && (
          <Suspense fallback={null}>
            {canShowPrivateInviteBanner && (
              <div className="w-full max-w-[1680px] mb-3 animate-in fade-in duration-500">
                <div className="bg-cyan-500/10 border border-cyan-400/30 rounded-xl p-3 xs:p-4 flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-cyan-400 animate-pulse">⏳</span>
                    <span className="text-sm text-cyan-200">Waiting for your opponent to join. Share the invite link!</span>
                  </div>
                  <button
                    onClick={copyInviteLink}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-cyan-400/50 bg-cyan-500/20 px-3 py-1.5 text-sm font-medium text-cyan-100 hover:bg-cyan-500/30 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                    </svg>
                    Copy Invite Link
                  </button>
                </div>
              </div>
            )}

            {gameMode === 'online' && onlineError && (
              <div className="w-full max-w-[1680px] mb-3">
                <div className="text-xs xs:text-sm text-rose-300 bg-rose-500/10 border border-rose-400/30 rounded-lg p-2">{onlineError}</div>
              </div>
            )}

            {gameMode === 'online' && roomPresence && (
              <div className="w-full max-w-[1680px] mb-3">
                <div className="rounded-xl border border-cyan-400/20 bg-cyan-500/10 p-3 text-sm text-cyan-100 shadow-[0_0_0_2px_rgba(6,182,212,0.15)]">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-200/90">
                      Live Room Presence
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
                      <div className={`flex items-center gap-2 rounded-full border px-3 py-1.5 ${roomPresence.whiteConnected ? 'border-emerald-300/40 bg-emerald-500/15 text-emerald-50' : 'border-rose-300/70 bg-rose-500/20 text-rose-50'}`}>
                        <span className={`h-2.5 w-2.5 rounded-full ${roomPresence.whiteConnected ? 'bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.75)]' : 'bg-rose-400 shadow-[0_0_10px_rgba(251,113,133,0.7)]'}`} aria-hidden="true" />
                        <span className="font-semibold text-text-primary">
                          {roomPresence.whiteName ?? onlineRoom?.whiteName ?? 'White'}
                        </span>
                        <span className={`text-xs font-medium uppercase tracking-wide ${roomPresence.whiteConnected ? 'text-emerald-100/90' : 'text-rose-100/90'}`}>
                          {roomPresence.whiteConnected ? 'online' : 'offline'}
                        </span>
                      </div>
                      <div className={`flex items-center gap-2 rounded-full border px-3 py-1.5 ${roomPresence.blackConnected ? 'border-emerald-300/40 bg-emerald-500/15 text-emerald-50' : 'border-rose-300/70 bg-rose-500/20 text-rose-50'}`}>
                        <span className={`h-2.5 w-2.5 rounded-full ${roomPresence.blackConnected ? 'bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.75)]' : 'bg-rose-400 shadow-[0_0_10px_rgba(251,113,133,0.7)]'}`} aria-hidden="true" />
                        <span className="font-semibold text-text-primary">
                          {roomPresence.blackName ?? onlineRoom?.blackName ?? 'Black'}
                        </span>
                        <span className={`text-xs font-medium uppercase tracking-wide ${roomPresence.blackConnected ? 'text-emerald-100/90' : 'text-rose-100/90'}`}>
                          {roomPresence.blackConnected ? 'online' : 'offline'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {gameRecordFeedback && (
              <div className="w-full max-w-[1680px] mb-3">
                <div className="rounded-xl border border-emerald-400/20 bg-emerald-500/10 p-3 text-sm text-emerald-100 shadow-[0_0_0_2px_rgba(52,211,153,0.12)]">
                  {gameRecordFeedback}
                </div>
              </div>
            )}

            <div className="w-full max-w-[1680px] mb-3">
              <BackgroundMusicPlayer />
            </div>

            {isCompactMode ? (
              // COMPACT MODE LAYOUT (for mobile/tablet)
              <div className="flex flex-col gap-2 xs:gap-3 w-full justify-center items-center animate-in fade-in zoom-in duration-500">
                {/* Single line game variation header */}
                <GameVariationHeader
                  gameVariation={gameVariation}
                  promotionRule={promotionRule}
                  playerPieces={playerPieces}
                />
                
                {/* Chess board without top/bottom player info */}
                <ChessBoard
                  board={board}
                  currentPlayer={currentPlayer}
                  selectedSquare={selectedSquare}
                  validMoves={validMoves}
                  lastMove={gameHistory.length > 0 ? gameHistory[gameHistory.length - 1] : null}
                  draggedPiece={draggedPiece}
                  draggedFrom={draggedFrom}
                  isDragging={isDragging}
                  isTouchDevice={isTouchDevice}
                  gameStatus={gameStatus.status}
                  flipped={gameMode === 'online' && onlineRoom?.role === 'black'}
                  whiteName={onlineRoom?.whiteName ?? null}
                  blackName={onlineRoom?.blackName ?? null}
                  whiteTimeMs={whiteTimeMs}
                  blackTimeMs={blackTimeMs}
                     formatTime={formatTime}
                     turnStartMs={turnStartMs}
                     timeControl={timeControl}
                     gamePhase={gamePhase}
                  onSquareClick={handleSquareClick}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onDragEnd={handleDragEnd}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                />
                
                {/* Game controls */}
                <GameControls
                  gameStatus={gameStatus}
                  isTouchDevice={isTouchDevice}
                  gameVariation={gameVariation}
                  promotionRule={promotionRule}
                  playerPieces={playerPieces}
                  onResetGame={handleResetOrLeave}
                  onResign={() => {
                    const color: Color = gameMode === 'online' && participantRole
                      ? participantRole
                      : currentPlayer;
                    resignGame(color);
                  }}
                  isOnline={gameMode === 'online'}
                  drawOfferQueued={offerDrawOnNextMove}
                  canQueueDrawOffer={canQueueDrawOffer}
                  hasIncomingDrawOffer={hasIncomingDrawOffer}
                  hasOutgoingDrawOffer={hasOutgoingDrawOffer}
                  canAcceptDrawOffer={hasIncomingDrawOffer && wsConnected}
                  onQueueDrawOffer={() => {
                    queueDrawOffer();
                    setOnlineError(null);
                    setOnlineStatus('Your next move will include a draw offer.');
                  }}
                  onCancelQueuedDrawOffer={() => {
                    cancelQueuedDrawOffer();
                    setOnlineError(null);
                    setOnlineStatus('Draw offer cancelled.');
                  }}
                  onAcceptDrawOffer={() => {
                    acceptDrawOffer();
                    setOnlineError(null);
                    setOnlineStatus('Draw accepted.');
                  }}
                  onLeaveOnline={gameMode === 'online' ? handleLeaveOnlineRoom : undefined}
                   onForceTimerStart={handleForceTimerStart}
                   canForceTimerStart={canForceTimerStart}
                />
                
                {/* Move history - always visible but smaller */}
                <MoveHistory
                  gameHistory={gameHistory}
                  compactMode={true}
                  onCopyGame={handleCopyGameRecord}
                  onDownloadGame={handleDownloadGameRecord}
                />
              </div>
            ) : (
              // ORIGINAL DESKTOP LAYOUT (unchanged)
              <div className="flex flex-col xl:flex-row gap-3 xs:gap-4 sm:gap-6 w-full justify-center items-stretch xl:items-start animate-in fade-in zoom-in duration-500">
                <div className="flex flex-col w-full xl:w-auto items-center xl:items-start">
                  <GameControls
                    gameStatus={gameStatus}
                    isTouchDevice={isTouchDevice}
                    gameVariation={gameVariation}
                    promotionRule={promotionRule}
                    playerPieces={playerPieces}
                    onResetGame={handleResetOrLeave}
                    onResign={() => {
                      const color: Color = gameMode === 'online' && participantRole
                        ? participantRole
                        : currentPlayer;
                      resignGame(color);
                    }}
                    isOnline={gameMode === 'online'}
                    drawOfferQueued={offerDrawOnNextMove}
                    canQueueDrawOffer={canQueueDrawOffer}
                    hasIncomingDrawOffer={hasIncomingDrawOffer}
                    hasOutgoingDrawOffer={hasOutgoingDrawOffer}
                    canAcceptDrawOffer={hasIncomingDrawOffer && wsConnected}
                    onQueueDrawOffer={gameHistory.length > 0 ? () => {
                      queueDrawOffer();
                      setOnlineError(null);
                      setOnlineStatus('Your next move will include a draw offer.');
                    } : undefined}
                    onCancelQueuedDrawOffer={() => {
                      cancelQueuedDrawOffer();
                      setOnlineError(null);
                      setOnlineStatus('Draw offer cancelled.');
                    }}
                    onAcceptDrawOffer={() => {
                      acceptDrawOffer();
                      setOnlineError(null);
                      setOnlineStatus('Draw accepted.');
                    }}
                    onLeaveOnline={gameMode === 'online' ? handleLeaveOnlineRoom : undefined}
                    onForceTimerStart={handleForceTimerStart}
                    canForceTimerStart={canForceTimerStart}
                  />

                  <ChessBoard
                    board={board}
                    currentPlayer={currentPlayer}
                    selectedSquare={selectedSquare}
                    validMoves={validMoves}
                    lastMove={gameHistory.length > 0 ? gameHistory[gameHistory.length - 1] : null}
                    draggedPiece={draggedPiece}
                    draggedFrom={draggedFrom}
                    isDragging={isDragging}
                    isTouchDevice={isTouchDevice}
                    gameStatus={gameStatus.status}
                    flipped={gameMode === 'online' && onlineRoom?.role === 'black'}
                    whiteName={onlineRoom?.whiteName ?? null}
                    blackName={onlineRoom?.blackName ?? null}
                    whiteTimeMs={whiteTimeMs}
                    blackTimeMs={blackTimeMs}
                    formatTime={formatTime}
                    turnStartMs={turnStartMs}
                    gamePhase={gamePhase}
                    onSquareClick={handleSquareClick}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onDragEnd={handleDragEnd}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                  />
                </div>

                <MoveHistory
                  gameHistory={gameHistory}
                  onCopyGame={handleCopyGameRecord}
                  onDownloadGame={handleDownloadGameRecord}
                />
              </div>
            )}
          </Suspense>
        )}
      </main>

      <Suspense fallback={null}>
        <PromotionDialog
          isOpen={gamePhase === 'promotion'}
          currentPlayer={currentPlayer}
          promotionOptions={getPromotionPieces()}
          onPromote={handlePromotion}
        />
      </Suspense>

      <footer className="mt-6 xs:mt-8 sm:mt-10 text-slate-400 text-xs xs:text-sm text-center w-full max-w-5xl border-t border-cyan-400/8 pt-4 xs:pt-5 sm:pt-7">
        <p>© {new Date().getFullYear()} Chess960x32. All rights reserved.</p>
        <div className="mt-3 xs:mt-4">
          <a
            href="https://github.com/sponsors/topce"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-cyan-400/40 bg-cyan-500/10 px-4 py-2 font-medium text-cyan-200 transition-colors hover:bg-cyan-500/20 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-slate-950"
            aria-label="Sponsor on GitHub"
          >
            <svg className="h-4 w-4" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
              <path d="M8 14.25a.74.74 0 0 1-.53-.22L1.84 8.27a4 4 0 0 1 5.66-5.65L8 3.12l.5-.5a4 4 0 1 1 5.66 5.65l-5.63 5.76a.74.74 0 0 1-.53.22Z" />
            </svg>
            Sponsor on GitHub
          </a>
        </div>
        <p className="mt-1 xs:mt-2 text-slate-400">Designed for the future of chess.</p>
      </footer>
    </div>
  );
};

export default Chess;
